// ─────────────────────────────────────────────────────────────────────────────
// EnrichCompanyEmailsCardComponent
// ─────────────────────────────────────────────────────────────────────────────
// Inline "Save unknown emails" suggestion card shown in the AI analysis tab,
// under Intent detection. Lets the user save a parsed sender email to the matched
// company's Email Registry — or, since WB-7092, to a specific location of that
// company — without leaving the order.
//
// Scope selector: a "Shipping Location" dropdown defaults to "Undefined" (the
// whole company). Picking a location writes to that location's registry instead
// (POST /v1/location-emails); the offered options are filtered against the
// CURRENTLY selected scope's registry, so we never suggest an address the target
// scope already knows. Company saves use POST /v1/company-emails. Duplicates
// (HTTP 409) are treated as benign.
//
// The company registry is owned by the parent (loaded once and shared with the
// sender row's "saved" check icon) and passed in via `registeredEmails`. On save
// it emits `saved` with the target scope so the parent can update the company
// registry (and light the sender row's check icon) only for company-scoped saves.
// ─────────────────────────────────────────────────────────────────────────────

import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

import { ButtonComponent } from 'src/app/ui/button/button.component';
import { SvgIconComponent } from 'src/app/ui/svg-icon/svg-icon.component';
import { CommonService } from 'src/app/shared/common.service';
import { CustomerEmailService } from 'src/app/features/knowledge-base/services/customer-email.service';
import {
  ICreateCustomerEmailPayload,
  ICreateLocationEmailPayload,
  IStructuredApiError,
  STRUCTURED_ERROR_CODES,
} from 'src/app/features/knowledge-base/models/customer-email.models';
import { ILocation } from 'src/app/features/customers/models/customers.models';
import { IOrderCompany } from '../../models/quotations.models';

/** Emitted on a successful save; `locationId` null means it went to the company. */
export interface IEnrichCardSavedEvent {
  email: string;
  locationId: number | null;
}

@Component({
  selector: 'app-enrich-company-emails-card',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    TranslateModule,
    ButtonComponent,
    SvgIconComponent,
  ],
  templateUrl: './enrich-company-emails-card.component.html',
  styleUrls: ['./enrich-company-emails-card.component.scss'],
})
export class EnrichCompanyEmailsCardComponent implements OnChanges, OnDestroy {

  /** AI-parsed email addresses to offer (sender only, for now). */
  @Input() emails: string[] = [];
  /** Matched company information (wl.company) — the registry owner. */
  @Input() company?: IOrderCompany | null;
  /** Lowercased emails already in the company registry (ERP + Volta), owned by the parent. */
  @Input() registeredEmails: Set<string> = new Set();
  /** Whether the registry has finished loading — gates visibility to avoid a flash. */
  @Input() registryLoaded = false;
  /** The matched company's locations, for the Shipping Location dropdown. */
  @Input() locations: ILocation[] = [];

  @Output() saved = new EventEmitter<IEnrichCardSavedEvent>();
  @Output() dismissed = new EventEmitter<void>();

  options: string[] = [];
  selectedEmail = '';
  /** null = company-level (default, "Undefined"); otherwise the location id. */
  selectedLocationId: number | null = null;
  isSaving = false;

  private parsedEmails: string[] = [];
  private isDismissed = false;
  /** Registry of the selected location (lowercased) — empty when company-scoped. */
  private locationRegistry = new Set<string>();
  /** Addresses saved within this card session — always dropped from options. */
  private readonly locallySaved = new Set<string>();
  private readonly destroy$ = new Subject<void>();

  constructor(
    private customerEmailService: CustomerEmailService,
    private commonService: CommonService,
    private translateService: TranslateService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['emails']) {
      const seen = new Set<string>();
      this.parsedEmails = (this.emails ?? [])
        .map((email) => (email ?? '').trim())
        .filter((email) => {
          const key = email.toLowerCase();
          if (!email || seen.has(key)) return false;
          seen.add(key);
          return true;
        });
    }
    // A new company invalidates all scope state — the selected location, its
    // fetched registry, and the locally-saved set all belonged to the PREVIOUS
    // company. Without this reset a stale location id could be saved against the
    // wrong company after an order is re-matched.
    if (changes['company'] && !changes['company'].firstChange
      && changes['company'].previousValue?.id !== changes['company'].currentValue?.id) {
      this.selectedLocationId = null;
      this.locationRegistry = new Set();
      this.locallySaved.clear();
    }
    if (changes['emails'] || changes['registeredEmails'] || changes['company']) {
      this.recomputeOptions();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Visible only once the registry is loaded and there is a not-yet-registered email to add. */
  get visible(): boolean {
    return this.registryLoaded && !this.isDismissed && this.options.length > 0 && !!this.company?.id;
  }

  /** Locations offered in the dropdown — only those with a usable id (a location
   * without an id would bind [ngValue]="undefined" and be misread as company scope). */
  get selectableLocations(): ILocation[] {
    return this.locations.filter((location) => location.id != null);
  }

  /** Re-filter the offered options against the newly selected scope's registry
   * (the company registry when no location is chosen, otherwise that location's
   * own registry, fetched on demand). */
  onLocationChange(): void {
    if (this.selectedLocationId == null) {
      this.locationRegistry = new Set();
      this.recomputeOptions();
      return;
    }
    const locationId = this.selectedLocationId;
    this.customerEmailService.listByLocation(locationId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (this.selectedLocationId !== locationId) return; // superseded
          this.locationRegistry = new Set(
            (response?.data ?? [])
              .map((row) => (row.email ?? '').trim().toLowerCase())
              .filter(Boolean),
          );
          this.recomputeOptions();
        },
        // On failure, treat the location's registry as unknown (empty) rather than
        // keeping the previous scope's — so we never pre-filter against the wrong
        // scope. A same-scope duplicate is benign on save (idempotent / 409).
        error: () => {
          if (this.selectedLocationId !== locationId) return;
          this.locationRegistry = new Set();
          this.recomputeOptions();
        },
      });
  }

  save(): void {
    if (this.isSaving || !this.selectedEmail || !this.company?.id) return;
    this.isSaving = true;

    const request$ = this.selectedLocationId != null
      ? this.customerEmailService.createLocation(
          { location_id: this.selectedLocationId, email: this.selectedEmail } as ICreateLocationEmailPayload)
      : this.customerEmailService.create(
          { customer_id: this.company.id, email: this.selectedEmail } as ICreateCustomerEmailPayload);

    request$
      .pipe(finalize(() => { this.isSaving = false; }))
      .subscribe({
        next: () => this.onSaved(),
        error: (error: IStructuredApiError) => {
          // Already in the registry → idempotent, treat as saved.
          if (error?.code === STRUCTURED_ERROR_CODES.DUPLICATE) {
            this.onSaved();
            return;
          }
          this.commonService.showToast(
            this.translateService.instant('quotations_and_orders.enrich_company_emails.error'),
            'ERROR',
          );
        },
      });
  }

  discard(): void {
    this.isDismissed = true;
    this.dismissed.emit();
  }

  /** Options = parsed emails not already in the active scope's registry and not
   * saved earlier in this card session. The active registry is the location's
   * when one is selected, otherwise the company's. */
  private recomputeOptions(): void {
    const activeRegistry = this.selectedLocationId != null
      ? this.locationRegistry
      : this.registeredEmails;
    this.options = this.parsedEmails.filter((email) => {
      const key = email.toLowerCase();
      return !activeRegistry.has(key) && !this.locallySaved.has(key);
    });
    if (!this.selectedEmail || !this.options.includes(this.selectedEmail)) {
      this.selectedEmail = this.options[0] ?? '';
    }
  }

  private onSaved(): void {
    const savedEmail = this.selectedEmail;
    this.commonService.showToast(
      this.translateService.instant('quotations_and_orders.enrich_company_emails.success'),
      'SUCCESS',
    );
    // Always drop the saved address locally so the option leaves and the card
    // hides once nothing remains, regardless of scope.
    this.locallySaved.add(savedEmail.toLowerCase());
    // Company-scoped saves also update the parent's shared registry (which lights
    // the sender row's check icon); location saves stay local to this card.
    this.saved.emit({ email: savedEmail, locationId: this.selectedLocationId });
    this.recomputeOptions();
  }

  /** Human label for a location option: name, plus city/country when available. */
  locationLabel(location: ILocation): string {
    const parts = [location.name, location.city, location.country?.name]
      .map((part) => (part ?? '').trim())
      .filter(Boolean);
    return parts.join(' - ') || (location.name ?? '');
  }
}
